'use client'

import { useEffect, useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { cmsStore, applyPendingEdits, applyPendingObjectEdits, type CmsStoreState } from '#/lib/cms-store'

/**
 * Returns true only after the component has mounted on the client.
 * Used to gate localStorage-backed content so SSR and the first client render
 * stay identical and React can hydrate cleanly.
 */
function useMounted() {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  return mounted
}

/**
 * Replace any image paths in an object's string values with pending data URLs.
 */
function resolveImages<T>(data: T, pendingImages: Record<string, string>): T {
  if (Object.keys(pendingImages).length === 0) return data

  if (Array.isArray(data)) {
    return data.map((item) => resolveImages(item, pendingImages)) as T
  }

  if (data && typeof data === 'object') {
    const result = { ...data } as Record<string, unknown>
    for (const [key, val] of Object.entries(result)) {
      if (typeof val === 'string' && pendingImages[val]) {
        result[key] = pendingImages[val]
      }
    }
    return result as T
  }

  return data
}

/**
 * Merge deployed array data with any pending CMS edits.
 */
export function useCmsContent<T extends { id: string }>(
  file: string,
  deployed: T[],
): T[] {
  const state = useStore(cmsStore, (s: CmsStoreState) => s)
  const mounted = useMounted()
  if (!mounted) return deployed
  const merged = applyPendingEdits(deployed as unknown as Record<string, unknown>[], file, state) as unknown as T[]
  return resolveImages(merged, state.pendingImages)
}

/**
 * Merge deployed object data (e.g. site-config) with pending edits.
 */
export function useCmsObjectContent<T>(
  file: string,
  deployed: T,
): T {
  const state = useStore(cmsStore, (s: CmsStoreState) => s)
  const mounted = useMounted()
  if (!mounted) return deployed
  const merged = applyPendingObjectEdits(deployed as unknown as Record<string, unknown>, file, state) as unknown as T
  return resolveImages(merged, state.pendingImages)
}
